import { supabase } from "./supabase";
import type {
  AccessRow,
  AuditFilters,
  AuditRecord,
  BootstrapData,
  DashboardData,
  DealInput,
  DealRecord,
  PendingActivity,
  PendingConversionInput,
  PendingDashboardData,
  PendingInput,
  PendingRecord,
  PendingScope,
  Role,
  SearchFilters
} from "./types";

function mapDeal(row: Record<string, unknown>): DealRecord {
  return {
    id: String(row.id),
    dealId: String(row.deal_id),
    date: String(row.deal_date),
    timeSlotId: String(row.time_slot_id),
    timeLabel: String(row.time_label),
    timeSort: Number(row.time_sort),
    department: row.department as DealRecord["department"],
    departmentName: String(row.department_name),
    customer: String(row.customer),
    vehicle: String(row.vehicle),
    stockNumber: String(row.stock_number),
    salespersonId: String(row.salesperson_id),
    salesperson: String(row.salesperson),
    fiManagerId: String(row.fi_manager_id),
    fiManager: String(row.fi_manager),
    typeNotes: String(row.type_notes ?? ""),
    status: row.status as DealRecord["status"],
    version: Number(row.version),
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at)
  };
}

function mapAudit(row: Record<string, unknown>): AuditRecord {
  return {
    id: Number(row.id),
    dealUuid: String(row.deal_uuid),
    dealId: String(row.deal_id),
    action: row.action as AuditRecord["action"],
    actorEmail: String(row.actor_email),
    reason: String(row.reason ?? ""),
    beforeData: (row.before_data as AuditRecord["beforeData"]) ?? null,
    afterData: (row.after_data as AuditRecord["afterData"]) ?? null,
    createdAt: String(row.created_at)
  };
}

function mapPending(row: Record<string, unknown>): PendingRecord {
  return {
    id: String(row.id),
    pendingId: String(row.pending_id),
    kind: row.kind as PendingRecord["kind"],
    customer: String(row.customer),
    vehicle: String(row.vehicle),
    stockNumber: String(row.stock_number),
    salespersonId: String(row.salesperson_id),
    salesperson: String(row.salesperson),
    fiManagerId: String(row.fi_manager_id),
    fiManager: String(row.fi_manager),
    expectedDate: row.expected_date ? String(row.expected_date) : "",
    deliveryTime: row.delivery_time ? String(row.delivery_time).slice(0, 5) : "",
    dealStatus: row.deal_status as PendingRecord["dealStatus"],
    financeStatus: row.finance_status as PendingRecord["financeStatus"],
    depositStatus: row.deposit_status as PendingRecord["depositStatus"],
    depositAmount: Number(row.deposit_amount ?? 0),
    depositDue: Number(row.deposit_due ?? 0),
    nextFollowUpDate: row.next_follow_up_date ? String(row.next_follow_up_date) : "",
    latestNote: String(row.latest_note ?? ""),
    linkedDealUuid: row.linked_deal_uuid ? String(row.linked_deal_uuid) : "",
    linkedDealId: row.linked_deal_id ? String(row.linked_deal_id) : "",
    closureStatus: row.closure_status as PendingRecord["closureStatus"],
    closureReason: String(row.closure_reason ?? ""),
    closureRequestedAt: row.closure_requested_at ? String(row.closure_requested_at) : "",
    closureRequestedByEmail: String(row.closure_requested_by_email ?? ""),
    version: Number(row.version),
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
    closedAt: row.closed_at ? String(row.closed_at) : ""
  };
}

function mapPendingActivity(row: Record<string, unknown>): PendingActivity {
  return {
    id: Number(row.id),
    action: row.action as PendingActivity["action"],
    actorEmail: String(row.actor_email),
    note: String(row.note ?? ""),
    beforeData: (row.before_data as PendingActivity["beforeData"]) ?? null,
    afterData: (row.after_data as PendingActivity["afterData"]) ?? null,
    createdAt: String(row.created_at)
  };
}

async function rpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const {data, error} = await supabase.rpc(name, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export const api = {
  bootstrap: () => rpc<BootstrapData>("bootstrap_app"),

  dashboard: async (date: string): Promise<DashboardData> => {
    const data = await rpc<Record<string, unknown>>("get_dashboard", {p_date: date});
    return {
      date: String(data.date),
      month: String(data.month),
      today: data.today as DashboardData["today"],
      monthToDate: data.monthToDate as DashboardData["monthToDate"],
      deals: ((data.deals ?? []) as Record<string, unknown>[]).map(mapDeal)
    };
  },

  createDeal: async (requestId: string, input: DealInput): Promise<DealRecord> => {
    const result = await rpc<{deal: Record<string, unknown>}>("create_deal", {
      p_request_id: requestId,
      p_deal_date: input.date,
      p_time_slot_id: input.timeSlotId,
      p_department: input.department,
      p_customer: input.customer,
      p_vehicle: input.vehicle,
      p_stock_number: input.stockNumber,
      p_salesperson_id: input.salespersonId,
      p_fi_manager_id: input.fiManagerId,
      p_type_notes: input.typeNotes
    });
    return mapDeal(result.deal);
  },

  updateDeal: async (
    requestId: string,
    existing: DealRecord,
    input: DealInput,
    reason: string
  ): Promise<DealRecord> => {
    const result = await rpc<{deal: Record<string, unknown>}>("update_deal", {
      p_request_id: requestId,
      p_deal_uuid: existing.id,
      p_expected_version: existing.version,
      p_deal_date: input.date,
      p_time_slot_id: input.timeSlotId,
      p_department: input.department,
      p_customer: input.customer,
      p_vehicle: input.vehicle,
      p_stock_number: input.stockNumber,
      p_salesperson_id: input.salespersonId,
      p_fi_manager_id: input.fiManagerId,
      p_type_notes: input.typeNotes,
      p_reason: reason
    });
    return mapDeal(result.deal);
  },

  removeDeal: async (
    requestId: string,
    deal: DealRecord,
    reason: string
  ): Promise<DealRecord> => {
    const result = await rpc<{deal: Record<string, unknown>}>("remove_deal", {
      p_request_id: requestId,
      p_deal_uuid: deal.id,
      p_expected_version: deal.version,
      p_reason: reason
    });
    return mapDeal(result.deal);
  },

  restoreDeal: async (
    requestId: string,
    deal: DealRecord,
    reason: string
  ): Promise<DealRecord> => {
    const result = await rpc<{deal: Record<string, unknown>}>("restore_deal", {
      p_request_id: requestId,
      p_deal_uuid: deal.id,
      p_expected_version: deal.version,
      p_reason: reason
    });
    return mapDeal(result.deal);
  },

  searchDeals: async (filters: SearchFilters, offset = 0, limit = 100): Promise<DealRecord[]> => {
    const rows = await rpc<Record<string, unknown>[]>("search_deals", {
      p_date_from: filters.dateFrom || null,
      p_date_to: filters.dateTo || null,
      p_query: filters.query || null,
      p_department: filters.department || null,
      p_salesperson_id: filters.salespersonId || null,
      p_include_removed: filters.includeRemoved,
      p_limit: limit,
      p_offset: offset
    });
    return rows.map(mapDeal);
  },

  listAudit: async (filters: AuditFilters, limit = 500): Promise<AuditRecord[]> => {
    let query = supabase
      .from("deal_audit")
      .select("id,deal_uuid,deal_id,action,actor_email,reason,before_data,after_data,created_at")
      .order("created_at", {ascending: false})
      .limit(limit);

    if (filters.dateFrom) query = query.gte("created_at", `${filters.dateFrom}T00:00:00`);
    if (filters.dateTo) query = query.lte("created_at", `${filters.dateTo}T23:59:59.999`);
    if (filters.action) query = query.eq("action", filters.action);

    const {data, error} = await query;
    if (error) throw new Error(error.message);

    const rows = ((data ?? []) as Record<string, unknown>[]).map(mapAudit);
    const term = filters.query.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) => {
      const searchable = [
        row.dealId,
        row.actorEmail,
        row.reason,
        JSON.stringify(row.beforeData ?? {}),
        JSON.stringify(row.afterData ?? {})
      ].join(" ").toLowerCase();
      return searchable.includes(term);
    });
  },

  pendingDashboard: async (date: string): Promise<PendingDashboardData> => {
    const data = await rpc<Record<string, unknown>>("get_pending_dashboard", {p_date: date});
    return {
      totalOpen: Number(data.totalOpen ?? 0),
      futureDeals: Number(data.futureDeals ?? 0),
      callInsAwaitingApproval: Number(data.callInsAwaitingApproval ?? 0),
      approvedNotDelivered: Number(data.approvedNotDelivered ?? 0),
      depositsDue: Number(data.depositsDue ?? 0),
      followUpsDue: Number(data.followUpsDue ?? 0),
      tomorrowsDeliveries: Number(data.tomorrowsDeliveries ?? 0),
      closurePending: Number(data.closurePending ?? 0)
    };
  },

  searchPending: async (
    scope: PendingScope,
    query: string,
    asOf: string,
    deliveryDate = "",
    limit = 250
  ): Promise<PendingRecord[]> => {
    const rows = await rpc<Record<string, unknown>[]>("search_pending_deals", {
      p_scope: scope,
      p_query: query || null,
      p_as_of: asOf,
      p_delivery_date: deliveryDate || null,
      p_limit: limit
    });
    return rows.map(mapPending);
  },

  createPending: async (requestId: string, input: PendingInput): Promise<PendingRecord> => {
    const result = await rpc<{pending: Record<string, unknown>}>("create_pending_deal", {
      p_request_id: requestId,
      p_kind: input.kind,
      p_customer: input.customer,
      p_vehicle: input.vehicle,
      p_stock_number: input.stockNumber,
      p_salesperson_id: input.salespersonId,
      p_fi_manager_id: input.fiManagerId,
      p_expected_date: input.expectedDate || null,
      p_delivery_time: input.deliveryTime || null,
      p_deal_status: input.dealStatus,
      p_finance_status: input.financeStatus,
      p_deposit_status: input.depositStatus,
      p_deposit_amount: input.depositAmount,
      p_deposit_due: input.depositDue,
      p_next_follow_up_date: input.nextFollowUpDate || null,
      p_note: input.note
    });
    return mapPending(result.pending);
  },

  updatePending: async (requestId: string, existing: PendingRecord, input: PendingInput): Promise<PendingRecord> => {
    const result = await rpc<{pending: Record<string, unknown>}>("update_pending_deal", {
      p_request_id: requestId,
      p_pending_uuid: existing.id,
      p_expected_version: existing.version,
      p_kind: input.kind,
      p_customer: input.customer,
      p_vehicle: input.vehicle,
      p_stock_number: input.stockNumber,
      p_salesperson_id: input.salespersonId,
      p_fi_manager_id: input.fiManagerId,
      p_expected_date: input.expectedDate || null,
      p_delivery_time: input.deliveryTime || null,
      p_deal_status: input.dealStatus,
      p_finance_status: input.financeStatus,
      p_deposit_status: input.depositStatus,
      p_deposit_amount: input.depositAmount,
      p_deposit_due: input.depositDue,
      p_next_follow_up_date: input.nextFollowUpDate || null,
      p_note: input.note
    });
    return mapPending(result.pending);
  },



  approvePendingClosure: async (
    requestId: string,
    pending: PendingRecord,
    note: string
  ): Promise<PendingRecord> => {
    const result = await rpc<{pending: Record<string, unknown>}>("approve_pending_closure", {
      p_request_id: requestId,
      p_pending_uuid: pending.id,
      p_expected_version: pending.version,
      p_note: note
    });
    return mapPending(result.pending);
  },

  returnPendingToActive: async (
    requestId: string,
    pending: PendingRecord,
    note: string
  ): Promise<PendingRecord> => {
    const result = await rpc<{pending: Record<string, unknown>}>("return_pending_to_active", {
      p_request_id: requestId,
      p_pending_uuid: pending.id,
      p_expected_version: pending.version,
      p_note: note
    });
    return mapPending(result.pending);
  },

  pendingActivity: async (pendingId: string, limit = 200): Promise<PendingActivity[]> => {
    const rows = await rpc<Record<string, unknown>[]>("get_pending_activity", {
      p_pending_uuid: pendingId,
      p_limit: limit
    });
    return rows.map(mapPendingActivity);
  },

  convertPending: async (
    requestId: string,
    pending: PendingRecord,
    input: PendingConversionInput
  ): Promise<{pending: PendingRecord; deal: DealRecord}> => {
    const result = await rpc<{pending: Record<string, unknown>; deal: Record<string, unknown>}>("convert_pending_to_deal", {
      p_request_id: requestId,
      p_pending_uuid: pending.id,
      p_expected_version: pending.version,
      p_deal_date: input.date,
      p_time_slot_id: input.timeSlotId,
      p_department: input.department,
      p_type_notes: input.typeNotes
    });
    return {pending: mapPending(result.pending), deal: mapDeal(result.deal)};
  },

  listAccess: () => rpc<AccessRow[]>("admin_list_access"),

  saveAccess: (input: {email: string; name: string; role: Role; active: boolean}) =>
    rpc("admin_upsert_access", {
      p_email: input.email,
      p_full_name: input.name,
      p_role: input.role,
      p_active: input.active
    })
};
